import React, { useState, useRef, useEffect } from 'react'
import { OmInput, OmDropdownMenu, type OmMenuItem } from '@owma/kit'
import { store as reduxStore } from 'index'
import i18n from 'i18next'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { RegExpTime } from 'pages/calendar/components/EventEditor/CustomTimeSelector'

import chevronDownIcon from 'assets/images/chevron-down-24.svg'

import styles from '../../style.module.scss'

export const DayjsTo12Hour = (time: Date | Dayjs): string => {
  const hourValue = dayjs(time).hour()
  const minuteValue = dayjs(time).minute()

  const hour12 = hourValue % 12 === 0 ? 12 : hourValue % 12
  const period = hourValue < 12 ? i18n.t('calendar.time.AM') : i18n.t('calendar.time.PM')
  const paddedMinute = String(minuteValue).padStart(2, '0')

  const timeLabel = `${hour12}:${paddedMinute} ${period}`
  return timeLabel
}

export const DayjsTo24Hour = (time: Date | Dayjs): string => {
  const hourValue = dayjs(time).hour()
  const minuteValue = dayjs(time).minute()

  const paddedHour = String(hourValue).padStart(2, '0')
  const paddedMinute = String(minuteValue).padStart(2, '0')

  const timeLabel = `${paddedHour}:${paddedMinute}`
  return timeLabel
}

interface CustomDatePickerProps {
  timeValue: Date | null
  setTimeValue: (time: Date) => void
  type: 'since' | 'until'
  disabled?: boolean
  since?: Date | null
  _data?: any
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  timeValue,
  setTimeValue,
  type,
  disabled = false,
  since,
  _data,
}) => {
  const { t } = useTranslation()
  const { settings } = reduxStore.getState() as any
  const { timeFormat: settingTimeFormat } = settings.general
  const is12HourFormat = settingTimeFormat === 'h:mm A'
  const isTypeSince = type === 'since'
  const formattedTime = is12HourFormat ? DayjsTo12Hour : DayjsTo24Hour

  const dropdownItems = (): OmMenuItem[] => {
    const isSameDay = since && dayjs(since).isSame(timeValue, 'day')

    return Array.from({ length: 24 }, (_, i) => {
      let label = ''

      if (is12HourFormat) {
        const hour = i % 12 === 0 ? 12 : i % 12
        const period = i < 12 ? i18n.t('calendar.time.AM') : i18n.t('calendar.time.PM')
        label = `${hour}:00 ${period}`
      } else {
        label = `${i}:00`
      }

      return {
        key: `${i}:00`,
        label: <div className="dropdownItems">{label}</div>,
      }
    }).filter(({ key }) => {
      const currentHour = dayjs(key, 'HH:mm').hour()
      const sinceHour = since && dayjs(since).hour()

      if (isSameDay && sinceHour && sinceHour > currentHour) return false
      return true
    })
  }

  const [value, setValue] = useState<string | null>(timeValue ? formattedTime(timeValue) : null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const scrollToSelected = (): void => {
    if (dropdownRef.current) {
      setTimeout(() => {
        const ANT_DROPDOWN_SELECTED = 'om-dropdown-menu-item-selected'
        const timeItems = dropdownRef.current!.querySelectorAll('.dropdownItems')
        const selectedItems = dropdownRef.current!.querySelectorAll(`.${ANT_DROPDOWN_SELECTED}`)

        selectedItems.forEach((el) => el.classList.remove(ANT_DROPDOWN_SELECTED))

        const format = dayjs(timeValue).format('H:mm')
        const selectedItem = dropdownItems().find((item) => item.key === format)?.label

        if (timeItems.length > 0 && selectedItem) {
          for (const item of Array.from(timeItems)) {
            const normalizedItemText = item.textContent || ''
            if (normalizedItemText.startsWith((selectedItem as any).props.children)) {
              const parentElement = item.closest('.om-dropdown-menu-item')
              if (parentElement) {
                parentElement.classList.add(ANT_DROPDOWN_SELECTED)
                parentElement.scrollIntoView({ block: 'start', inline: 'nearest' })
                break
              }
            }
          }
        }
      }, 100)
    }
  }

  const handleChangeTime = ({
    key,
    inputValue,
  }: {
    key?: string | null
    inputValue?: string | null
  }): void => {
    let realTime: string | null = null
    if (key != null) {
      const labelValue = dropdownItems().find((item) => item.key === key)?.label
      realTime = (labelValue as any)?.props.children
    } else {
      realTime = inputValue || null
    }

    if (!realTime) return

    const [hours, period] = realTime.split(' ')
    const [hourStr, minuteStr] = hours.split(':')

    let hour = parseInt(hourStr, 10)
    const minute = parseInt(minuteStr, 10)

    if (period === t('calendar.time.AM') && hour === 12) hour = 0
    if (period === t('calendar.time.PM') && hour !== 12) hour += 12

    const newSince = new Date(timeValue || new Date())
    newSince.setHours(hour)
    newSince.setMinutes(minute)
    newSince.setSeconds(0)
    newSince.setMilliseconds(0)

    setValue(realTime)
    setTimeValue(newSince)
    console.log(' --- since --- ', dayjs(timeValue))
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const newValue = (e.target as HTMLInputElement).value
    const isInvalidUntil =
      type === 'until' && (since && dayjs(since).isBefore(dayjs(timeValue))) === false

    if (RegExpTime.get12Hours().test(newValue) === false || isInvalidUntil) {
      setValue(timeValue ? formattedTime(timeValue) : null)
      return
    }

    handleChangeTime({ key: null, inputValue: newValue })
    setValue(newValue)
  }

  useEffect(() => {
    if (isTypeSince || !since || !timeValue) return

    const isPastDay = dayjs(since).isBefore(timeValue)
    if (!isPastDay) {
      setValue(formattedTime(since))
    }
  }, [since, timeValue])

  useEffect(() => {
    if (type === 'until') {
      setValue(timeValue ? formattedTime(timeValue) : null)
    }
  }, [disabled])

  return (
    <div className={styles.customDatePicker}>
      <OmInput
        className={styles.timeInput}
        value={(timeValue && value) || ''}
        disabled={disabled}
        onChange={(val) => setValue(val)}
        onBlur={handleBlur}
      />
      <OmDropdownMenu
        menu={{
          items: dropdownItems(),
          onClick: ({ key }: { key: string }) => handleChangeTime({ key, inputValue: null }),
        }}
        overlayClassName={classNames('contextMenu', styles.dropdownOverlay)}
        align={{ offset: [-70, 10] }}
        trigger={['click']}
        placement="bottom"
        popupRender={(menu: React.ReactNode) => <div ref={dropdownRef}>{menu}</div>}
        onOpenChange={(open: boolean) => {
          if (open) {
            setTimeout(() => scrollToSelected(), 50)
          }
        }}
      >
        <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
      </OmDropdownMenu>
    </div>
  )
}

export default CustomDatePicker
